import { clsx } from 'clsx';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';

/* -------------------------------------------------------------------------
   Button

   Three variants and two sizes, deliberately. A control plane with six button
   variants is a control plane where nobody can tell which action is destructive.
   ------------------------------------------------------------------------- */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends ComponentPropsWithoutRef<'button'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
}

const BUTTON_BASE =
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-[var(--vc-radius-md)] ' +
  'font-medium transition-colors duration-[var(--vc-duration-fast)] ' +
  'disabled:cursor-not-allowed disabled:opacity-50 ' +
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--vc-focus-ring)]';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-[var(--vc-accent)] text-[var(--vc-accent-contrast)] hover:bg-[var(--vc-accent-hover)]',
  secondary:
    'bg-[var(--vc-surface)] text-[var(--vc-text)] border border-[var(--vc-border-strong)] ' +
    'hover:bg-[var(--vc-surface-hover)]',
  ghost:
    'text-[var(--vc-text-muted)] hover:bg-[var(--vc-surface-hover)] hover:text-[var(--vc-text)]',
  danger: 'bg-[var(--vc-danger-solid)] text-white hover:opacity-90',
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: 'h-7 px-2.5 text-[12px]',
  md: 'h-8 px-3 text-[13px]',
};

export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={clsx(BUTTON_BASE, BUTTON_VARIANTS[variant], BUTTON_SIZES[size], className)}
      {...rest}
    >
      {icon ? (
        <span aria-hidden="true" className="[&>svg]:size-3.5 shrink-0">
          {icon}
        </span>
      ) : null}
      {children}
    </button>
  );
}

/* -------------------------------------------------------------------------
   StatusBadge

   Status is conveyed by label first and colour second. §33: no information may
   be carried by colour alone, and a licensing console is exactly where a
   colour-blind operator must not misread REVOKED as ACTIVE.
   ------------------------------------------------------------------------- */

export type StatusTone = 'success' | 'warning' | 'danger' | 'neutral' | 'info';

export interface StatusBadgeProps {
  tone?: StatusTone;
  children: ReactNode;
  /** Adds a filled dot. Purely decorative — the label always carries the meaning. */
  dot?: boolean;
  className?: string;
}

const TONE_STYLES: Record<StatusTone, { chip: string; dot: string }> = {
  success: {
    chip: 'bg-[var(--vc-success-bg)] text-[var(--vc-success-text)] border-[var(--vc-success-border)]',
    dot: 'bg-[var(--vc-success-solid)]',
  },
  warning: {
    chip: 'bg-[var(--vc-warning-bg)] text-[var(--vc-warning-text)] border-[var(--vc-warning-border)]',
    dot: 'bg-[var(--vc-warning-solid)]',
  },
  danger: {
    chip: 'bg-[var(--vc-danger-bg)] text-[var(--vc-danger-text)] border-[var(--vc-danger-border)]',
    dot: 'bg-[var(--vc-danger-solid)]',
  },
  neutral: {
    chip: 'bg-[var(--vc-neutral-bg)] text-[var(--vc-neutral-text)] border-[var(--vc-neutral-border)]',
    dot: 'bg-[var(--vc-text-subtle)]',
  },
  info: {
    chip:
      'bg-[var(--vc-accent-subtle-bg)] text-[var(--vc-accent-subtle-text)] ' +
      'border-[var(--vc-accent-subtle-border)]',
    dot: 'bg-[var(--vc-accent)]',
  },
};

export function StatusBadge({
  tone = 'neutral',
  dot = true,
  children,
  className,
}: StatusBadgeProps) {
  const styles = TONE_STYLES[tone];
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-[var(--vc-radius-full)] border px-2 py-0.5',
        'text-[11px] font-medium tracking-[0.01em] whitespace-nowrap',
        styles.chip,
        className,
      )}
    >
      {dot ? (
        <span aria-hidden="true" className={clsx('size-1.5 rounded-full', styles.dot)} />
      ) : null}
      {children}
    </span>
  );
}

/* -------------------------------------------------------------------------
   Environment kind and classification

   Which environment you are looking at is the single most consequential piece
   of context in this console, so it gets its own component rather than a
   colour convention people are expected to remember.
   ------------------------------------------------------------------------- */

export type EnvironmentKind =
  'DEVELOPMENT' | 'TEST' | 'STAGING' | 'PILOT' | 'PRODUCTION' | 'CUSTOM';

export type EnvironmentClassification = 'NON_PRODUCTION' | 'PRODUCTION';

const KIND_LABEL: Record<EnvironmentKind, string> = {
  DEVELOPMENT: 'Development',
  TEST: 'Test',
  STAGING: 'Staging',
  PILOT: 'Pilot',
  PRODUCTION: 'Production',
  CUSTOM: 'Custom',
};

/**
 * Kind is a word, not a colour.
 *
 * It used to render as a coloured badge, which quietly made it look like the
 * important field. It is not: it describes what the vendor calls this stage of
 * their process, and nothing keys off it. The value that carries meaning is
 * classification, and giving both the same visual weight taught the wrong
 * thing.
 */
export function environmentKindLabel(kind: EnvironmentKind): string {
  return KIND_LABEL[kind] ?? kind;
}

const CLASSIFICATION_LABEL: Record<EnvironmentClassification, string> = {
  PRODUCTION: 'Production',
  NON_PRODUCTION: 'Non-production',
};

/**
 * The load-bearing one, and the only environment field that gets a badge.
 *
 * Production is drawn in the accent tone, not in danger red. Red means "this
 * went wrong"; a production environment is the normal, desirable end state of
 * setting a product up. Marking it as a hazard would teach operators to read
 * their healthiest environment as a problem.
 *
 * The label is always present, so the meaning never depends on distinguishing
 * two colours.
 */
export function ClassificationBadge({
  classification,
  className,
}: {
  classification: EnvironmentClassification;
  className?: string;
}) {
  return (
    <StatusBadge tone={classification === 'PRODUCTION' ? 'info' : 'neutral'} className={className}>
      {CLASSIFICATION_LABEL[classification]}
    </StatusBadge>
  );
}

/* -------------------------------------------------------------------------
   Mono — the identifier treatment

   Every id, key prefix and slug in the product renders through this. Consistent
   monospace treatment is what lets an operator recognise "that is an identifier
   I can copy" without reading the label.
   ------------------------------------------------------------------------- */

export function Mono({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <code
      className={clsx(
        'font-[family-name:var(--vc-font-mono)] text-[12px] text-[var(--vc-text-muted)]',
        className,
      )}
    >
      {children}
    </code>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd
      className={clsx(
        'inline-flex h-5 min-w-5 items-center justify-center rounded-[var(--vc-radius-sm)]',
        'border border-[var(--vc-border)] bg-[var(--vc-surface-sunken)] px-1.5',
        'font-[family-name:var(--vc-font-mono)] text-[11px] text-[var(--vc-text-muted)]',
      )}
    >
      {children}
    </kbd>
  );
}
